import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { selectActualFeatureFlags } from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import {
  selectAllowedMspRoleIdFilter,
  selectAssignedCompaniesData,
  selectAssignedCompaniesMspRoleFilter,
  selectAssignedCompaniesPLanTypeFilter,
  selectAssignedCompaniesSearchFilter,
  selectAssignedSelectionData,
  selectIsToAssignDrawerOpen,
  selectManageableCompaniesData,
  selectSortProperty,
  selectToAssignCompaniesData,
  selectToAssignCompaniesSearchFilter,
  selectToAssignPlanTypeFilter,
  selectToAssignSelectionData,
  selectUserInfo,
  selectUserToUpdate,
} from 'containers/Settings/Users/selectors'
import { addToast } from 'containers/Toasts/actions'

import {
  addCompanyUserError,
  addCompanyUserSuccess,
  assignCompaniesError,
  assignCompaniesSuccess,
  delCompanyUserError,
  delCompanyUserSuccess,
  fetchCncAccessError,
  fetchCncAccessSuccess,
  fetchMailboxDataError,
  fetchMailboxDataSuccess,
  getCompanyUsersError,
  getCompanyUsersSuccess,
  getManageableCompaniesError,
  getManageableCompaniesSuccess,
  getManageableUserAssignedCompaniesError,
  getManageableUserAssignedCompaniesSuccess,
  getManageableUserToAssignCompaniesError,
  getManageableUserToAssignCompaniesSuccess,
  getProfilesError,
  getProfilesSuccess,
  getUserInfoError,
  getUserInfoSuccess,
  makeOwnerError,
  makeOwnerSuccess,
  unassignMSPCompaniesError,
  unassignMSPCompaniesSuccess,
  updateCncAccessError,
  updateCncAccessSuccess,
  updCompanyUserError,
  updCompanyUserSuccess,
} from './actions'
import {
  ADD_USER_REQUEST_ASYNC,
  ASSIGN_COMPANIES_ASYNC,
  ASSIGNED_COMPANIES_PER_PAGE,
  ASSIGNED_USER_COMPANIES_PER_PAGE,
  CHANGE_ALLOWED_MSP_ROLE_ID_FILTER,
  CHANGE_ASSIGNED_COMPANIES_MSP_ROLE_FILTER,
  CHANGE_ASSIGNED_COMPANIES_PLAN_TYPE_FILTER,
  CHANGE_ASSIGNED_COMPANIES_SEARCH_FILTER,
  CHANGE_TO_ASSIGN_COMPANIES_SEARCH_FILTER,
  CHANGE_TO_ASSIGN_PLAN_TYPE_FILTER,
  CNC_ACCESS_REQUEST_ASYNC,
  COMPANY_USERS_REQUEST_ASYNC,
  DATA_REQUEST_ASYNC,
  DELETE_USER_REQUEST_ASYNC,
  GET_MANAGEABLE_COMPANIES_ASYNC,
  GET_USER_INFO_ASYNC,
  GET_USER_MANAGEABLE_ASSIGNED_COMPANIES_ASYNC,
  GET_USER_MANAGEABLE_TO_ASSIGN_COMPANIES_ASYNC,
  MAKE_OWNER_ASYNC,
  PROFILES_REQUEST_ASYNC,
  SEARCH_CHANGE,
  UNASSIGN_COMPANIES_ASYNC,
  UPDATE_CNC_ACCESS_REQUEST_ASYNC,
  UPDATE_USER_REQUEST_ASYNC,
} from './constants'

const getCompanyUsersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      COMPANY_USERS_REQUEST_ASYNC.INITIATE,
      ADD_USER_REQUEST_ASYNC.SUCCESS,
      DELETE_USER_REQUEST_ASYNC.SUCCESS,
      UPDATE_USER_REQUEST_ASYNC.SUCCESS,
      MAKE_OWNER_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.permissions)
      return true
    }),
    mergeMap(() => {
      let assignedCompaniesValue = selectManageableCompaniesData(state$.value).value

      if (!assignedCompaniesValue.length) {
        assignedCompaniesValue = null
      }

      return api.settings
        .getCompanyUsers(
          state$.value.partnerOptions.selectedCompany?.value,
          selectSortProperty(state$.value),
          assignedCompaniesValue
        )
        .pipe(
          map((response) => getCompanyUsersSuccess(response)),
          catchError((error) => of(epicsErrorHandler(getCompanyUsersError, error))),
          takeUntil(action$.pipe(ofType(COMPANY_USERS_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

const getProfilesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      PROFILES_REQUEST_ASYNC.INITIATE,
      ADD_USER_REQUEST_ASYNC.SUCCESS,
      DELETE_USER_REQUEST_ASYNC.SUCCESS,
      UPDATE_USER_REQUEST_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.permissions)
      return true
    }),
    mergeMap(() =>
      api.settings.getProfiles(state$.value.partnerOptions.selectedCompany?.value).pipe(
        map((response) => getProfilesSuccess(response.data)),
        catchError((error) => of(epicsErrorHandler(getProfilesError, error))),
        takeUntil(action$.pipe(ofType(PROFILES_REQUEST_ASYNC.CANCEL)))
      )
    )
  )

const delCompanyUserEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_USER_REQUEST_ASYNC.INITIATE),
    mergeMap(() =>
      api.settings
        .delCompanyUser(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.settings.users.self.userToDelete
        )
        .pipe(
          mergeMap((response) =>
            of(
              delCompanyUserSuccess(response),
              addToast({
                message: 'User was deleted successfully',
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(delCompanyUserError, error),
              addToast({
                message: error.response?.message || error?.response,
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(DELETE_USER_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const addCompanyUserEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(ADD_USER_REQUEST_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings
        .addCompanyUser(state$.value.partnerOptions.selectedCompany?.value, action.payload.user)
        .pipe(
          mergeMap((response) =>
            of(
              addCompanyUserSuccess(response),
              addToast({
                message: 'User was added successfully',
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(addCompanyUserError, error),
              addToast({
                message: error.response?.message || error?.response,
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(ADD_USER_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const updCompanyUserEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_USER_REQUEST_ASYNC.INITIATE),
    mergeMap(() => {
      const userToUpdate = selectUserToUpdate(state$.value)
      // TODO: remove when we will get rid of snake_case
      const user = {
        //
        id: userToUpdate.id,
        email: userToUpdate.email,
        last_name: userToUpdate.lastName || userToUpdate.last_name,
        first_name: userToUpdate.firstName || userToUpdate.first_name,
        ioc_access: userToUpdate.iocAccess ?? userToUpdate.ioc_access,
        phishing_soc_access: userToUpdate.phishingSocAccess,
        deepfake_presume_safe_access:
          userToUpdate.deepfakePresumeSafeAccess ?? userToUpdate.deepfake_presume_safe_access,
        is_owner: userToUpdate.isOwner || userToUpdate.is_owner,
        role: userToUpdate.role,
        user_type: userToUpdate.userType || userToUpdate.user_type,
        company_id: userToUpdate.companyId || userToUpdate.company_id,
      }

      return api.settings
        .updCompanyUser(state$.value.partnerOptions.selectedCompany?.value, user)
        .pipe(
          mergeMap((response) =>
            of(
              updCompanyUserSuccess(response),
              addToast({
                message: 'User was updated successfully',
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(updCompanyUserError, error),
              addToast({
                message: error.response?.message || error?.response,
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(UPDATE_USER_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

const makeOwnerEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(MAKE_OWNER_ASYNC.INITIATE),
    mergeMap(() =>
      api.settings
        .makeOwner(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.settings.users.self.userToUpdate
        )
        .pipe(
          mergeMap((response) =>
            of(
              makeOwnerSuccess(response),
              addToast({
                message: 'User was updated successfully',
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(makeOwnerError, error),
              addToast({
                message: error.response?.message || error?.response,
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(MAKE_OWNER_ASYNC.CANCEL)))
        )
    )
  )

const fetchMailboxDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SEARCH_CHANGE),
    mergeMap((action) =>
      api.mailboxes
        .get(
          undefined,
          '',
          undefined,
          undefined,
          '',
          '',
          '',
          action.payload.q,
          state$.value.partnerOptions.selectedCompany?.value
        )
        .pipe(
          map((response) => fetchMailboxDataSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchMailboxDataError, error, [400]))),
          takeUntil(action$.pipe(ofType(DATA_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const fetchCncAccessDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      CNC_ACCESS_REQUEST_ASYNC.INITIATE,
      UPDATE_CNC_ACCESS_REQUEST_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      const ff = selectActualFeatureFlags(state$.value)
      if (!ff.DISABLE_CNC_ACCESS) return false
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.permissions)
      return true
    }),
    mergeMap(() =>
      api.settings.getCncAccess(state$.value.partnerOptions.selectedCompany?.value).pipe(
        map((response) => fetchCncAccessSuccess(response.cncAccess)),
        catchError((error) => of(epicsErrorHandler(fetchCncAccessError, error, [400]))),
        takeUntil(action$.pipe(ofType(CNC_ACCESS_REQUEST_ASYNC.CANCEL)))
      )
    )
  )

const setCncAccessDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_CNC_ACCESS_REQUEST_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings
        .setCncAccess(action.payload.cncAccess, state$.value.partnerOptions.selectedCompany?.value)
        .pipe(
          map(() => updateCncAccessSuccess()),
          catchError((error) => of(epicsErrorHandler(updateCncAccessError, error, [400]))),
          takeUntil(action$.pipe(ofType(UPDATE_CNC_ACCESS_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const getManageableCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_MANAGEABLE_COMPANIES_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings
        .getManageableCompanies({
          companyId: state$.value.partnerOptions.selectedCompany?.value,
          page: action.payload.params.page,
          pageSize: ASSIGNED_COMPANIES_PER_PAGE,
          search: action.payload.params.search,
        })
        .pipe(
          map((response) => getManageableCompaniesSuccess(response)),
          catchError((error) => of(epicsErrorHandler(getManageableCompaniesError, error))),
          takeUntil(action$.pipe(ofType(GET_MANAGEABLE_COMPANIES_ASYNC.CANCEL)))
        )
    )
  )

const getUserInfoEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_USER_INFO_ASYNC.INITIATE,
      ASSIGN_COMPANIES_ASYNC.SUCCESS,
      UNASSIGN_COMPANIES_ASYNC.SUCCESS
    ),
    mergeMap((action) =>
      api.settings
        .getUserInfo(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload?.id || selectUserInfo(state$.value).data.orgUserId
        )
        .pipe(
          map((response) => getUserInfoSuccess(response)),
          catchError((error) => of(epicsErrorHandler(getUserInfoError, error))),
          takeUntil(action$.pipe(ofType(GET_USER_INFO_ASYNC.CANCEL)))
        )
    )
  )

const getManageableUserCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_USER_MANAGEABLE_ASSIGNED_COMPANIES_ASYNC.INITIATE,
      GET_USER_MANAGEABLE_TO_ASSIGN_COMPANIES_ASYNC.INITIATE,
      CHANGE_ASSIGNED_COMPANIES_SEARCH_FILTER,
      CHANGE_ASSIGNED_COMPANIES_MSP_ROLE_FILTER,
      CHANGE_ASSIGNED_COMPANIES_PLAN_TYPE_FILTER,
      CHANGE_ALLOWED_MSP_ROLE_ID_FILTER,
      CHANGE_TO_ASSIGN_PLAN_TYPE_FILTER,
      CHANGE_TO_ASSIGN_COMPANIES_SEARCH_FILTER,
      ASSIGN_COMPANIES_ASYNC.SUCCESS,
      UNASSIGN_COMPANIES_ASYNC.SUCCESS
    ),
    switchMap((action) => {
      const isToAssignDrawerOpen = selectIsToAssignDrawerOpen(state$.value)
      const url = new URL(window.location)
      const pathSegments = url.pathname.split('/')
      const id = pathSegments[pathSegments.length - 1]
      let isAssigned = !isToAssignDrawerOpen

      if (action.type === ASSIGN_COMPANIES_ASYNC.SUCCESS) {
        isAssigned = true
      }

      const params = {
        id,
        companyId: state$.value.partnerOptions.selectedCompany?.value,
        pageSize: ASSIGNED_USER_COMPANIES_PER_PAGE,
        isAssigned,
      }

      const assignedParams = {
        ...params,
        page: selectAssignedCompaniesData(state$.value).page,
        company: selectAssignedCompaniesSearchFilter(state$.value),
        mspRoleIds: selectAssignedCompaniesMspRoleFilter(state$.value),
        planTypes: selectAssignedCompaniesPLanTypeFilter(state$.value),
      }
      const unAssignedParams = {
        ...params,
        page: selectToAssignCompaniesData(state$.value).page,
        company: selectToAssignCompaniesSearchFilter(state$.value),
        allowedMspRoleId: selectAllowedMspRoleIdFilter(state$.value),
        planTypes: selectToAssignPlanTypeFilter(state$.value),
      }

      const successAction = isAssigned
        ? getManageableUserAssignedCompaniesSuccess
        : getManageableUserToAssignCompaniesSuccess
      const errorAction = isAssigned
        ? getManageableUserAssignedCompaniesError
        : getManageableUserToAssignCompaniesError

      return api.settings
        .getManageableUserCompanies(isAssigned ? assignedParams : unAssignedParams)
        .pipe(
          map((response) => successAction(response)),
          catchError((error) => of(epicsErrorHandler(errorAction, error)))
        )
    })
  )

const assignCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(ASSIGN_COMPANIES_ASYNC.INITIATE),
    mergeMap(() => {
      const url = new URL(window.location)
      const pathSegments = url.pathname.split('/')
      const id = pathSegments[pathSegments.length - 1]
      const selectionData = selectToAssignSelectionData(state$.value)
      const data = {
        selectionData: selectionData.allItems
          ? {
              allItems: true,
              excludeIds: selectionData.selectionIds,
            }
          : {
              allItems: false,
              includeIds: selectionData.selectionIds,
              excludeIds: [],
            },
        mspRoleId: selectAllowedMspRoleIdFilter(state$.value),
      }
      const planTypes = selectToAssignPlanTypeFilter(state$.value)
      const company = selectToAssignCompaniesSearchFilter(state$.value)

      if (planTypes.length) {
        data.planTypes = planTypes
      }

      if (company) {
        data.company = company
      }

      return api.settings
        .assignCompanies({
          id,
          companyId: state$.value.partnerOptions.selectedCompany?.value,
          data,
        })
        .pipe(
          mergeMap(() => {
            const toAssignCompaniesData = selectToAssignCompaniesData(state$.value)
            const count = selectionData.allItems
              ? toAssignCompaniesData.meta.count
              : selectionData.selectionIds.length

            return of(
              assignCompaniesSuccess(),
              addToast({
                message: `${count} ${
                  selectionData.selectionIds.length > 1 ? 'companies' : 'company'
                } successfully assigned`,
              })
            )
          }),
          catchError((error) =>
            of(
              epicsErrorHandler(assignCompaniesError, error),
              addToast({
                message: error.response?.message || error?.response,
              })
            )
          ),
          takeUntil(action$.pipe(ofType(ASSIGN_COMPANIES_ASYNC.CANCEL)))
        )
    })
  )

const unassignCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UNASSIGN_COMPANIES_ASYNC.INITIATE),
    mergeMap(() => {
      const url = new URL(window.location)
      const pathSegments = url.pathname.split('/')
      const id = pathSegments[pathSegments.length - 1]
      const selectionData = selectAssignedSelectionData(state$.value)
      const data = {
        selectionData: selectionData.allItems
          ? {
              allItems: true,
              excludeIds: selectionData.selectionIds,
            }
          : {
              allItems: false,
              includeIds: selectionData.selectionIds,
              excludeIds: [],
            },
      }
      const company = selectAssignedCompaniesSearchFilter(state$.value)
      const mspRoleIds = selectAssignedCompaniesMspRoleFilter(state$.value)
      const planTypes = selectAssignedCompaniesPLanTypeFilter(state$.value)

      if (company) {
        data.company = company
      }

      if (mspRoleIds.length) {
        data.mspRoleIds = mspRoleIds
      }

      if (planTypes.length) {
        data.planTypes = planTypes
      }

      return api.settings
        .unassignMSPCompanies({
          id,
          companyId: state$.value.partnerOptions.selectedCompany?.value,
          data,
        })
        .pipe(
          mergeMap(() => {
            const assignedCompaniesData = selectAssignedCompaniesData(state$.value)
            const count = selectionData.allItems
              ? assignedCompaniesData.meta.count
              : selectionData.selectionIds.length

            return of(
              unassignMSPCompaniesSuccess(),
              addToast({
                message: `${count} ${
                  selectionData.selectionIds.length > 1 ? 'companies' : 'company'
                } successfully unassigned`,
              })
            )
          }),
          catchError((error) =>
            of(
              epicsErrorHandler(unassignMSPCompaniesError, error),
              addToast({
                message: error.response?.message || error?.response,
              })
            )
          ),
          takeUntil(action$.pipe(ofType(UNASSIGN_COMPANIES_ASYNC.CANCEL)))
        )
    })
  )

export default combineEpics(
  getCompanyUsersEpic,
  getProfilesEpic,
  delCompanyUserEpic,
  addCompanyUserEpic,
  updCompanyUserEpic,
  makeOwnerEpic,
  fetchMailboxDataEpic,
  fetchCncAccessDataEpic,
  setCncAccessDataEpic,
  getManageableCompaniesEpic,
  getUserInfoEpic,
  getManageableUserCompaniesEpic,
  assignCompaniesEpic,
  unassignCompaniesEpic
)
